import { useTranslation } from 'react-i18next';
import React, { useEffect, useContext, useState } from 'react';
import { NavContext } from '@/components/NavContext';
import {
  IconButton,
  TeamAvatar,
  Stepper,
  Button,
  InputSelect,
  InputText,
  Tabs,
} from '@teo/components';
import { CrossFilled } from '@teo/components/icons';
import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { axios } from '@/axios';
import { config } from '@/config';
import { Teams } from '@/models';
import { UserInviteComplete } from '@/components/UserInvite/UserInviteComplete';
import { T } from '@/components/T';
import { parseRole, useMobile, deep_copy } from '@/util';

const roles = [10, 20, 40, 45, 70, 90];

const langs = [
  {
    value: 'en',
    label: 'EN',
  },
  {
    value: 'nl',
    label: 'NL',
  },
  {
    value: 'fr',
    label: 'FR',
  },
  {
    value: 'es',
    label: 'ES',
  },
  {
    value: 'de',
    label: 'DE',
  },
];

function AddUsersToTeamManagePage() {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const { auth } = useContext(AuthContext);
  const { setNavDisabled } = useContext(NavContext);
  const { organisationId, teamId } = useParams();

  const navigate = useNavigate();

  const [team, setTeam] = useState<Teams | null>(null);

  const activeOrganisationId = auth?.user?.activeOrganisationId;
  const activeLang = team?.teamLang || auth?.user?.lang;

  const [step, setStep] = useState(1);
  const [userTypeTab, setUserTypeTab] = useState('students');

  const [newStudent, setNewStudent] = useState<any>([]);
  const [newTeacher, setNewTeacher] = useState<any>([]);

  const [isAddingUsers, setIsAddingUsers] = useState(false);

  const hasSelectedUsers = newStudent.length > 0 || newTeacher.length > 0;
  const hasNewUsers =
    newStudent.some((user: any) => user.newUser) ||
    newTeacher.some((user: any) => user.newUser);

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  });

  useGet(`/team/${organisationId}/${teamId}`, {
    onSuccess: (result: any) => {
      setTeam(result?.data || null);
    },
  });

  if (!team) return null;

  const handleAddStudentToOrganization = async () => {
    if (isAddingUsers) return;
    setIsAddingUsers(true);

    try {
      const usersToAdd = [
        ...newStudent.map((user: any) => ({ user, isTeamleader: false })),
        ...newTeacher.map((user: any) => ({ user, isTeamleader: true })),
      ];

      for (const { user: item, isTeamleader } of usersToAdd) {
        try {
          let user = item;

          // 1. Provision user first
          if (item.newUser) {
            const result = await axios.post(
              `${config.backend}/invite/provision?lang=${item?.lang}`,
              {
                organisationId: auth?.user?.activeOrganisationId,
                email: item.label,
                firstName: item.firstName,
                lastName: item.lastName,
                phone: item.phone,
                role: item.role || 20,
                lang: item?.lang,
              }
            );

            user = {
              ...result?.data,
              key: result?.data.id,
              role: item.role || 20,
              newUser: true,
            };
          }

          const minRole = user.role || 20;

          // 2. Add user as organisation member
          let member;

          try {
            member = await axios.post(`${config.backend}/members`, {
              organisationId: organisationId,
              userId: user.key,
              role: minRole,
            });
          } catch {
            member = await axios.get(
              `${config.backend}/member/${organisationId}/${user.key}`
            );
          }

          // 3. Raise member role if needed
          if (minRole > (member?.data?.role || 0)) {
            member = await axios.put(
              `${config.backend}/member/${organisationId}/${user.key}`,
              {
                role: minRole,
              }
            );
          }

          // 4. Add to team
          await axios
            .post(
              `${config.backend}/${
                isTeamleader ? 'teamteachers' : 'teammembers'
              }`,
              {
                teamId: teamId,
                userId: user.key,
                role: user.role,
              }
            )
            .catch((e) => console.error(e));
        } catch (e) {
          console.error(e);
        }
      }

      navigate(-1);
    } finally {
      setIsAddingUsers(false);
    }
  };

  return (
    <div className="fixed z-10 flex h-full min-h-[100vh] min-w-[100vw] flex-col bg-white">
      <div className="flex min-h-16 items-center justify-start ">
        <IconButton
          className="my-auto mx-6"
          Icon={CrossFilled}
          variant="ghost"
          onClick={() => navigate(-1)}
        />
        <div className="flex items-center">
          <TeamAvatar
            inline
            id={team.id}
            name={team.teamName || ''}
          ></TeamAvatar>
          <span>{t('pages.manage_add_users.add_users_to')}</span>
          <span className="pl-2 font-semibold">{team.teamName}</span>
        </div>
      </div>

      <div className="flex h-full flex-col border-t border-grey-02">
        <Stepper>
          <Stepper.Item
            isActive={step === 1}
            isDone={step > 1}
            onClick={() => setStep(1)}
            step="1"
          >
            {(!isMobile || step === 1) && (
              <T>{t('pages.manage_add_users.add_users_step_1')}</T>
            )}
          </Stepper.Item>
          {hasNewUsers && (
            <Stepper.Item
              isActive={step === 2}
              isDone={step > 2}
              onClick={() => setStep(2)}
              step="2"
            >
              {(!isMobile || step === 2) && (
                <T>{t('pages.manage_add_users.add_users_step_2')}</T>
              )}
            </Stepper.Item>
          )}
        </Stepper>
        {step === 1 && (
          <div className="h-full max-h-[calc(100vh_-_225px)] overflow-y-auto py-8">
            <div className="mx-auto flex max-w-screen-md flex-col justify-center px-3">
              <article className="prose flex flex-col">
                <h3 className="text-base">
                  {t('pages.manage_add_users.add_users_step_1')}
                </h3>
                <p className="pt-3 text-sm text-grey-07">
                  {t('pages.manage_add_users.subtitle')}
                </p>
              </article>

              <div className="mt-6 flex flex-col lg:flex-row">
                <Tabs
                  activeName={userTypeTab}
                  onChange={(tab) => setUserTypeTab(tab)}
                >
                  <div className="flex gap-2">
                    <Tabs.TabButton name="students">
                      <p className="text-base font-medium">
                        {t('pages.manage_add_users.students_tab')}
                      </p>
                    </Tabs.TabButton>
                    <Tabs.TabButton name="teamleaders">
                      <p className="text-base font-medium">
                        {t('pages.manage_add_users.teamleaders_tab')}
                      </p>
                    </Tabs.TabButton>
                  </div>
                </Tabs>
              </div>

              <div className="mt-1">
                {userTypeTab === 'students' && (
                  <UserInviteComplete
                    extra={{ role: 20 }}
                    fetch={useGet.bind(null, '/memberdetails', {
                      organisationId: activeOrganisationId,
                    })}
                    newUser={newStudent}
                    setNewUser={setNewStudent}
                  />
                )}

                {userTypeTab === 'teamleaders' && (
                  <UserInviteComplete
                    extra={{ role: 40 }}
                    fetch={useGet.bind(null, '/memberdetails', {
                      organisationId: activeOrganisationId,
                    })}
                    newUser={newTeacher}
                    setNewUser={setNewTeacher}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <>
            <div className="h-full max-h-[calc(100vh_-_225px)] overflow-y-auto py-8">
              <div className="mx-auto flex max-w-screen-md flex-col justify-center px-3">
                <span className="pb-2 text-base font-semibold">
                  {t('pages.manage_add_users.add_users_to_your_team')}
                </span>
                <span className="text-sm font-normal text-grey-06">
                  {t('pages.manage_add_users.add_additional_info')}
                </span>

                {isMobile ? (
                  <div className="mt-8 flex flex-col gap-3">
                    {newStudent.map(
                      (el: any, i: number) =>
                        el?.newUser && (
                          <div
                            key={el.key + '_2'}
                            className="flex max-w-[450px] flex-col rounded-lg border border-grey-02 p-4"
                          >
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.email_address')}
                            </p>
                            <div>
                              {el.newUser && (
                                <InputText
                                  disabled
                                  id="email"
                                  value={el.label}
                                  onChange={() => {}}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.firstname')}
                            </p>
                            <div>
                              {el.newUser && (
                                <InputText
                                  id="firstname"
                                  onChange={(event: any) => {
                                    const newUsers = deep_copy(newStudent);
                                    newUsers[i].firstName = event.target.value;
                                    setNewStudent(newUsers);
                                  }}
                                  value={el.firstName}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.lastname')}
                            </p>
                            <div>
                              {el.newUser && (
                                <InputText
                                  id="lastname"
                                  onChange={(event: any) => {
                                    const newUsers = deep_copy(newStudent);
                                    newUsers[i].lastName = event.target.value;
                                    setNewStudent(newUsers);
                                  }}
                                  value={el.lastName}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.role')}
                            </p>
                            <div className="mb-3">
                              {el.newUser && (
                                <InputSelect
                                  onChange={(role: number) => {
                                    const newUsers = deep_copy(newStudent);
                                    newUsers[i].role = role;
                                    setNewStudent(newUsers);
                                  }}
                                  value={el.role}
                                  options={roles}
                                >
                                  {(option) => (
                                    <span key={option}>
                                      {t(parseRole(option))}
                                    </span>
                                  )}
                                </InputSelect>
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.phone_number')}
                            </p>
                            <div className="mr-2">
                              {el.newUser && (
                                <InputText
                                  id="phone"
                                  onChange={(event: any) => {
                                    const newUsers = deep_copy(newStudent);
                                    newUsers[i].phone = event.target.value;
                                    setNewStudent(newUsers);
                                  }}
                                  value={el.phone}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.language')}
                            </p>
                            <div className="mb-3">
                              {el.newUser && (
                                <InputSelect
                                  onChange={(lang) => {
                                    const newUsers = deep_copy(newStudent);
                                    newUsers[i].lang = lang.value;
                                    setNewStudent(newUsers);
                                  }}
                                  value={
                                    el.lang
                                      ? langs.find(
                                          (lang) => lang?.value === el.lang
                                        )
                                      : langs.find(
                                          (lang) => lang?.value === activeLang
                                        )
                                  }
                                  options={langs}
                                >
                                  {(option) => option.label}
                                </InputSelect>
                              )}
                            </div>
                          </div>
                        )
                    )}
                    {newTeacher.map(
                      (el: any, i: number) =>
                        el?.newUser && (
                          <div
                            key={el.key + '_2'}
                            className="flex max-w-[450px] flex-col rounded-lg border border-grey-02 p-4"
                          >
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.email_address')}
                            </p>
                            <div>
                              {el.newUser && (
                                <InputText
                                  disabled
                                  id="email"
                                  value={el.label}
                                  onChange={() => {}}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.firstname')}
                            </p>
                            <div>
                              {el.newUser && (
                                <InputText
                                  id="firstname"
                                  onChange={(event: any) => {
                                    const newUsers = deep_copy(newTeacher);
                                    newUsers[i].firstName = event.target.value;
                                    setNewTeacher(newUsers);
                                  }}
                                  value={el.firstName}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.lastname')}
                            </p>
                            <div>
                              {el.newUser && (
                                <InputText
                                  id="lastname"
                                  onChange={(event: any) => {
                                    const newUsers = deep_copy(newTeacher);
                                    newUsers[i].lastName = event.target.value;
                                    setNewTeacher(newUsers);
                                  }}
                                  value={el.lastName}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.role')}
                            </p>
                            <div className="mb-3">
                              {el.newUser && (
                                <InputSelect
                                  onChange={(role: number) => {
                                    const newUsers = deep_copy(newTeacher);
                                    newUsers[i].role = role;
                                    setNewTeacher(newUsers);
                                  }}
                                  value={el.role}
                                  options={roles}
                                >
                                  {(option) => (
                                    <span key={option}>
                                      {t(parseRole(option))}
                                    </span>
                                  )}
                                </InputSelect>
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.phone_number')}
                            </p>
                            <div className="mr-2">
                              {el.newUser && (
                                <InputText
                                  id="phone"
                                  onChange={(event: any) => {
                                    const newUsers = deep_copy(newTeacher);
                                    newUsers[i].phone = event.target.value;
                                    setNewTeacher(newUsers);
                                  }}
                                  value={el.phone}
                                  className="pb-3"
                                />
                              )}
                            </div>
                            <p className="pb-1 text-sm font-semibold text-black">
                              {t('pages.manage_add_users.language')}
                            </p>
                            <div className="mb-3">
                              {el.newUser && (
                                <InputSelect
                                  onChange={(lang) => {
                                    const newUsers = deep_copy(newTeacher);
                                    newUsers[i].lang = lang.value;
                                    setNewTeacher(newUsers);
                                  }}
                                  value={
                                    el.lang
                                      ? langs.find(
                                          (lang) => lang?.value === el.lang
                                        )
                                      : langs.find(
                                          (lang) => lang?.value === activeLang
                                        )
                                  }
                                  options={langs}
                                >
                                  {(option) => option.label}
                                </InputSelect>
                              )}
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ) : (
                  <table className="mt-10 gap-2">
                    <tbody>
                      <tr>
                        <td className="text-sm font-semibold text-black">
                          {t('pages.manage_add_users.email_address')}
                        </td>
                        <td className="text-sm font-semibold text-black">
                          {t('pages.manage_add_users.firstname')}
                        </td>
                        <td className="text-sm font-semibold text-black">
                          {t('pages.manage_add_users.lastname')}
                        </td>
                        <td className="text-sm font-semibold text-black">
                          {t('pages.manage_add_users.role')}
                        </td>
                        <td className="text-sm font-semibold text-black">
                          {t('pages.manage_add_users.phone_number')}
                        </td>
                        <td className="text-sm font-semibold text-black">
                          {t('pages.manage_add_users.language')}
                        </td>
                      </tr>
                      {newStudent.map(
                        (el: any, i: number) =>
                          el?.newUser && (
                            <tr key={el.key + '_2'}>
                              <td>
                                <div>
                                  {el.newUser && (
                                    <InputText
                                      disabled
                                      id="email"
                                      value={el.label}
                                      onChange={() => {}}
                                      className="pb-5 pr-2"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {el.newUser && (
                                    <InputText
                                      id="firstname"
                                      onChange={(event: any) => {
                                        const newUsers = deep_copy(newStudent);
                                        newUsers[i].firstName =
                                          event.target.value;
                                        setNewStudent(newUsers);
                                      }}
                                      value={el.firstName}
                                      className="pb-5 pr-2"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {el.newUser && (
                                    <InputText
                                      id="lastname"
                                      onChange={(event: any) => {
                                        const newUsers = deep_copy(newStudent);
                                        newUsers[i].lastName =
                                          event.target.value;
                                        setNewStudent(newUsers);
                                      }}
                                      value={el.lastName}
                                      className="pb-5 pr-2"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="mb-[22px] mr-2 w-[160px]">
                                  {el.newUser && (
                                    <InputSelect
                                      onChange={(role: number) => {
                                        const newUsers = deep_copy(newStudent);
                                        newUsers[i].role = role;
                                        setNewStudent(newUsers);
                                      }}
                                      value={el.role}
                                      options={roles}
                                    >
                                      {(option) => (
                                        <span key={option}>
                                          {t(parseRole(option))}
                                        </span>
                                      )}
                                    </InputSelect>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="mr-2">
                                  {el.newUser && (
                                    <InputText
                                      id="phone"
                                      onChange={(event: any) => {
                                        const newUsers = deep_copy(newStudent);
                                        newUsers[i].phone = event.target.value;
                                        setNewStudent(newUsers);
                                      }}
                                      value={el.phone}
                                      className="pb-5"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="mb-[22px] ">
                                  {el.newUser && (
                                    <InputSelect
                                      onChange={(lang) => {
                                        const newUsers = deep_copy(newStudent);
                                        newUsers[i].lang = lang.value;
                                        setNewStudent(newUsers);
                                      }}
                                      value={
                                        el.lang
                                          ? langs.find(
                                              (lang) => lang?.value === el.lang
                                            )
                                          : langs.find(
                                              (lang) =>
                                                lang?.value === activeLang
                                            )
                                      }
                                      options={langs}
                                    >
                                      {(option) => option.label}
                                    </InputSelect>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                      )}
                      {newTeacher.map(
                        (el: any, i: number) =>
                          el?.newUser && (
                            <tr key={el.key + '_2'}>
                              <td>
                                <div>
                                  {el.newUser && (
                                    <InputText
                                      disabled
                                      id="email"
                                      value={el.label}
                                      onChange={() => {}}
                                      className="pb-5 pr-2"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {el.newUser && (
                                    <InputText
                                      id="firstname"
                                      onChange={(event: any) => {
                                        const newUsers = deep_copy(newTeacher);
                                        newUsers[i].firstName =
                                          event.target.value;
                                        setNewTeacher(newUsers);
                                      }}
                                      value={el.firstName}
                                      className="pb-5 pr-2"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {el.newUser && (
                                    <InputText
                                      id="lastname"
                                      onChange={(event: any) => {
                                        const newUsers = deep_copy(newTeacher);
                                        newUsers[i].lastName =
                                          event.target.value;
                                        setNewTeacher(newUsers);
                                      }}
                                      value={el.lastName}
                                      className="pb-5 pr-2"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="mb-[22px] mr-2 w-[160px]">
                                  {el.newUser && (
                                    <InputSelect
                                      onChange={(role: number) => {
                                        const newUsers = deep_copy(newTeacher);
                                        newUsers[i].role = role;
                                        setNewTeacher(newUsers);
                                      }}
                                      value={el.role}
                                      options={roles}
                                    >
                                      {(option) => (
                                        <span key={option}>
                                          {t(parseRole(option))}
                                        </span>
                                      )}
                                    </InputSelect>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="mr-2">
                                  {el.newUser && (
                                    <InputText
                                      id="phone"
                                      onChange={(event: any) => {
                                        const newUsers = deep_copy(newTeacher);
                                        newUsers[i].phone = event.target.value;
                                        setNewTeacher(newUsers);
                                      }}
                                      value={el.phone}
                                      className="pb-5"
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="mb-[22px] ">
                                  {el.newUser && (
                                    <InputSelect
                                      onChange={(lang) => {
                                        const newUsers = deep_copy(newTeacher);
                                        newUsers[i].lang = lang.value;
                                        setNewTeacher(newUsers);
                                      }}
                                      value={
                                        el.lang
                                          ? langs.find(
                                              (lang) => lang?.value === el.lang
                                            )
                                          : langs.find(
                                              (lang) =>
                                                lang?.value === activeLang
                                            )
                                      }
                                      options={langs}
                                    >
                                      {(option) => option.label}
                                    </InputSelect>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        <div className="mt-auto flex border-t border-grey-02 py-5">
          <div className="mx-auto flex w-full max-w-screen-md justify-end px-3">
            {step > 1 && (
              <Button
                variant="secondary"
                onClick={() => setStep(step - 1)}
                className="mr-auto"
              >
                {t('button.previous')}
              </Button>
            )}
            {step === 1 && hasNewUsers && (
              <Button disabled={!hasSelectedUsers} onClick={() => setStep(2)}>
                {t('pages.manage_add_users.next_button')}
              </Button>
            )}
            {(step === 2 || (step === 1 && !hasNewUsers)) && (
              <Button
                disabled={isAddingUsers || !hasSelectedUsers}
                onClick={() => handleAddStudentToOrganization()}
                data-testid="addUsersButton"
              >
                {t('pages.manage_add_users.add_users_button')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddUsersToTeamManagePage;
